import type { Idioma } from "@/lib/landing/idioma"

/**
 * Las direcciones del mercado.
 *
 * Siguen el mismo arbol que la guia de restaurantes —isla, municipio— para que
 * la localidad de cada vendedor este en la propia direccion:
 *
 *   /mercado/mallorca                       el mercado de la isla
 *   /mercado/mallorca/cafe                  una seccion: «Café local en Mallorca»
 *   /mercado/mallorca/palma                 lo que se vende en un municipio
 *   /mercado/mallorca/palma/7am             un vendedor
 *   /mercado/mallorca/palma/7am/rio-fuego   un producto
 *
 * El segundo tramo es una seccion o un municipio. No chocan: las secciones son
 * cinco palabras fijas (lib/mercado/categorias.ts) y ningun municipio se llama asi.
 */

export const RAIZ_DEL_MERCADO = "/mercado"

export function rutaMercado(...trozos: (string | null | undefined)[]): string {
  const limpios = trozos.filter((t): t is string => Boolean(t)).map((t) => encodeURIComponent(t))
  return [RAIZ_DEL_MERCADO, ...limpios].join("/")
}

export function rutaVendedor(v: { slug: string; lugar: { slug: string; isla: { slug: string } | null } }): string {
  return rutaMercado(v.lugar.isla?.slug ?? "mallorca", v.lugar.slug, v.slug)
}

export function rutaProducto(
  v: { slug: string; lugar: { slug: string; isla: { slug: string } | null } },
  productoSlug: string,
): string {
  return `${rutaVendedor(v)}/${encodeURIComponent(productoSlug)}`
}

export const RUTA_CESTA = "/mercado/cesta"

export function rutaPedido(token: string): string {
  return `/mercado/pedido/${encodeURIComponent(token)}`
}

/**
 * El mercado para negocios va en su propio arbol, «Distribuidores de café»:
 *
 *   /distribuidores/mallorca          los distribuidores de la isla
 *   /distribuidores/mallorca/7am      uno, con su tarifa para negocios
 *
 * Aparte del de cliente final a proposito: son dos publicos y dos precios, y no
 * se quiere que quien compra para casa aterrice en una tarifa de hosteleria.
 */
export const RAIZ_DE_DISTRIBUIDORES = "/distribuidores"

export function rutaDistribuidores(...trozos: (string | null | undefined)[]): string {
  const limpios = trozos.filter((t): t is string => Boolean(t)).map((t) => encodeURIComponent(t))
  return [RAIZ_DE_DISTRIBUIDORES, ...limpios].join("/")
}

export function rutaDistribuidor(v: { slug: string; lugar: { isla: { slug: string } | null } }): string {
  return rutaDistribuidores(v.lugar.isla?.slug ?? "mallorca", v.slug)
}

// ─────────────────────────────────────────────────────────────────────────────
// Las mismas, en los dos idiomas
// ─────────────────────────────────────────────────────────────────────────────

/**
 * En inglés el mercado cuelga de /en/market y los distribuidores de
 * /en/distributors, con el mismo árbol: los slugs de isla, municipio, vendedor
 * y producto son datos y no cambian. Lo único que se traduce es el trozo de las
 * secciones (cafe → coffee, lib/mercado/categorias.ts) y el de la cesta.
 */
export const RAIZ = {
  mercado: { es: RAIZ_DEL_MERCADO, en: "/en/market" },
  distribuidores: { es: RAIZ_DE_DISTRIBUIDORES, en: "/en/distributors" },
} as const

/** El trozo de la cesta en inglés: /en/market/basket. */
export const CESTA_EN = "basket"

function unir(raiz: string, trozos: (string | null | undefined)[]): string {
  const limpios = trozos.filter((t): t is string => Boolean(t)).map((t) => encodeURIComponent(t))
  return [raiz, ...limpios].join("/")
}

export function rutaDelMercado(idioma: Idioma, ...trozos: (string | null | undefined)[]): string {
  return unir(RAIZ.mercado[idioma], trozos)
}

export function rutaDeDistribuidores(idioma: Idioma, ...trozos: (string | null | undefined)[]): string {
  return unir(RAIZ.distribuidores[idioma], trozos)
}

type ConLugar = { slug: string; lugar: { slug: string; isla: { slug: string } | null } }

export function rutaDelVendedor(idioma: Idioma, v: ConLugar): string {
  return rutaDelMercado(idioma, v.lugar.isla?.slug ?? "mallorca", v.lugar.slug, v.slug)
}

export function rutaDelProducto(idioma: Idioma, v: ConLugar, productoSlug: string): string {
  return `${rutaDelVendedor(idioma, v)}/${encodeURIComponent(productoSlug)}`
}

export function rutaDeLaCesta(idioma: Idioma): string {
  return idioma === "en" ? rutaDelMercado("en", CESTA_EN) : RUTA_CESTA
}

export function rutaDelDistribuidor(idioma: Idioma, v: { slug: string; lugar: { isla: { slug: string } | null } }): string {
  return rutaDeDistribuidores(idioma, v.lugar.isla?.slug ?? "mallorca", v.slug)
}
